import { atom } from "jotai";
import type { GuardianScriptedReply } from "@/api/temp/TrustAIExecutiveService";
import type { ConversationEntry } from "@/components/AILanding/utils/messageUtils";
import type { GuardianContext } from "@/components/Guardian/guardianContext";

export type GuardianDisplayMode = "closed" | "floating" | "docked";

/**
 * Docked geometry, in one place because the rail's width, its minimum, the
 * resize ceiling and the shell's reserved margin have to agree — they are
 * read from TS and from `guardian-rail.scss`'s fallbacks.
 */
/** The rail never takes more than half the viewport. */
export const GUARDIAN_MAX_DOCKED_VW = 50;
/** Below this the rail is too narrow to read a transcript in. */
export const GUARDIAN_MIN_DOCKED_PX = 420;
/** Below this viewport width the docked rail is fixed-width, not resizable. */
export const GUARDIAN_DOCK_RESIZE_MIN_PX = 840;
/** Content width the shell insists on keeping before it reserves docked space. */
export const GUARDIAN_CONTENT_MIN_PX = 1280;

export interface GuardianRequest {
  kind: "introduction" | "question";
  context: GuardianContext;
  query: string;
  promptId: string | null;
}

export type GuardianEntry =
  | {
      kind: "message";
      id: string;
      context: GuardianContext;
      entry: ConversationEntry;
      request?: GuardianRequest;
      reply?: GuardianScriptedReply;
    }
  | {
      kind: "context-change";
      id: string;
      from: GuardianContext;
      to: GuardianContext;
      timestamp: number;
    };

export interface GuardianState {
  displayMode: GuardianDisplayMode;
  /**
   * The mode a reopen restores. Docking is a deliberate choice, so closing the
   * rail must not silently downgrade the next open back to floating — the same
   * reason `dockedWidthVw` survives a close.
   */
  lastOpenMode: Exclude<GuardianDisplayMode, "closed">;
  dockedWidthVw: number | null;
  context: GuardianContext;
  recentContexts: GuardianContext[];
  entries: GuardianEntry[];
  draft: string;
  focusVersion: number;
}

/** One in-memory atom per authenticated Guardian host; no cross-tenant cache. */
export const createGuardianSessionAtom = (context: GuardianContext) =>
  atom<GuardianState>({
    displayMode: "closed",
    lastOpenMode: "floating",
    dockedWidthVw: null,
    context,
    recentContexts: [],
    entries: [],
    draft: "",
    focusVersion: 0,
  });
